/**
 * Asset path utility for handling paths in both web and Electron environments.
 * 
 * In web builds, assets are served from the root (e.g., /os/assets/...).
 * In Electron builds with base: "./", paths need to be relative (e.g., ./os/assets/...).
 */

// Detect if running inside Electron
export const isElectron = typeof navigator !== 'undefined' && 
  navigator.userAgent.toLowerCase().includes('electron');

/**
 * Converts an absolute asset path to work in both web and Electron environments.
 * 
 * @param path - The asset path (e.g., '/os/assets/images/wallpapers/image.jpg')
 * @returns The corrected path for the current environment
 */
export function getAssetPath(path: string): string {
  if (!path) return path;
  
  // If it's already a relative path, external URL, or data URL, return as-is
  if (path.startsWith('./') || 
      path.startsWith('http://') || 
      path.startsWith('https://') || 
      path.startsWith('data:') ||
      path.startsWith('blob:')) {
    return path;
  }
  
  // In Electron, convert absolute paths to relative
  if (isElectron && path.startsWith('/')) {
    return '.' + path;
  }
  
  return path;
}

/**
 * Wallpaper definitions with paths that work in both environments.
 * Use this centralized list to avoid path duplication across components.
 */
export const WALLPAPERS = [
  {
    id: 'default',
    name: 'Classic Maple',
    url: '/os/assets/images/wallpapers/drops_maple_leaf_autumn_1319773_1600x900.jpg',
  },
  {
    id: 'edmonton',
    name: 'Edmonton Nights',
    url: '/os/assets/images/wallpapers/edmonton_canada_night_city_118462_1600x900.jpg',
  },
  {
    id: 'owl',
    name: 'Snow Owl',
    url: '/os/assets/images/wallpapers/owl_white_owl_polar_owl_136287_1600x900.jpg',
  },
  {
    id: 'toronto',
    name: 'Toronto Sky',
    url: '/os/assets/images/wallpapers/city_buildings_architecture_160579_1600x900.jpg',
  },
  {
    id: 'deer',
    name: 'Deer',
    url: '/os/assets/images/wallpapers/deer_wildlife_horns_131368_1600x900.jpg',
  },
  {
    id: 'cubes',
    name: '3D Cubes',
    url: '/os/assets/images/wallpapers/cubes_edges_abstraction_1312232_1600x900.jpg',
  },
] as const;

/**
 * Get wallpaper URL by ID, with correct path for current environment.
 */
export function getWallpaperUrl(id: string): string {
  const wallpaper = WALLPAPERS.find(w => w.id === id);
  if (!wallpaper) return getAssetPath(WALLPAPERS[0].url);
  return getAssetPath(wallpaper.url);
}

/**
 * Get wallpaper ID from a URL (handles both absolute and relative paths).
 */
export function getWallpaperIdFromUrl(url: string | null): string | null {
  if (!url) return null;
  
  // Normalize the URL for comparison (remove leading ./ or /)
  const normalizedUrl = url.replace(/^\.?\//, '/');
  
  for (const wallpaper of WALLPAPERS) {
    const normalizedWallpaperUrl = wallpaper.url.replace(/^\.?\//, '/');
    if (normalizedUrl === normalizedWallpaperUrl || 
        normalizedUrl.endsWith(wallpaper.url.split('/').pop() || '')) {
      return wallpaper.id;
    }
  }
  
  return null;
}

